import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector, batch } from 'react-redux'
import { addMonths } from 'date-fns'
import PropTypes from 'prop-types'
import { useViewportSize } from '../../modules/hooks/viewport'
import { formatDate } from '../../modules/utils/formatters'
import { onChangeIdleRpmGroupSelectedThunk, onGetIdleRpmThunk, onGetIdleRpmReportThunk } from './IdleRpm.actions'
import ConsumptionOperatorAlert from '../ConsumptionOperatorAlert/ConsumptionOperatorAlert.component'
import IdleRpmTable from './IdleRpm.table'
import Accordion from '../../components/Surfaces/Accordion'
import AccordionSummary from '../../components/Surfaces/AccordionSummary'
import Alert from '../../components/Basics/Alert'
import Skeleton from '../../components/Placeholders/Skeleton'
import Tag from '../../components/Basics/Tag'
import Typography from '../../components/Basics/Typography'
import Button from '../../components/Buttons/Button'
import { CSVLink } from 'react-csv'
import { AccordionDetails, Arrow, Root, Separator, Strong, SummaryWrapper, Wrapper, TitleInfo } from './IdleRpm.styles'

/**
 * The Idle RPM' container.
 */
const IdleRpm = props => {
  const dispatch = useDispatch()
  const { onToggleModal } = props
  const csvLink = useRef()
  const { isTiny } = useViewportSize()
  const {
    consultationDate,
    error,
    groups,
    loading,
    selectedGroupId,
    totalLitersOperators,
    meanIdleRpmTotal,
    p80Total,
    reportLoading,
    reportSuccess,
    reportData
  } = useSelector(state => state.idleRpm)
  const [expanded, setExpanded] = useState(selectedGroupId)
  const currentDate = addMonths(new Date(), -1)

  useEffect(() => {
    if (!loading) {
      return
    }

    batch(() => {
      dispatch(onGetIdleRpmThunk())
      dispatch(onGetIdleRpmReportThunk())
    })
  }, [])

  useEffect(() => {
    setExpanded(selectedGroupId)
  }, [selectedGroupId])

  const handleChange = panel => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
    dispatch(onChangeIdleRpmGroupSelectedThunk({ groupId: isExpanded ? panel : false }))
  }

  return (
    <Root>
      {consultationDate && formatDate(consultationDate, 'yyyy-MM') !== formatDate(currentDate, 'yyyy-MM') && (
        <ConsumptionOperatorAlert />
      )}

      <Wrapper>
        <Typography margin="0 0 10px" variant="h3">
          <Strong>Tiempo en Ralentí -</Strong> Operadores con tiempos superiores a la media
        </Typography>
        {loading && !error && (
          <Wrapper>
            <Skeleton height="63px" margin="0 0 10px" variant="rect" />
          </Wrapper>
        )}
        {!loading && !error && (
          <Wrapper margin="25 0 25 0">
            <TitleInfo>
              <Typography fontWeight="bold" variant="h4">
                INDICADORES FLOTA RALENTÍ VACIO
              </Typography>

              <Arrow />

              <SummaryWrapper margin="2px 30px 2px 0">
                <Tag color="primary" margin="0 10px 0 0" text={p80Total} />
                <Typography variant="body2">P80 tiempo</Typography>
              </SummaryWrapper>

              <SummaryWrapper margin="2px 30px 2px 0">
                <Tag color="primary" margin="0 10px 0 0" text={meanIdleRpmTotal} />
                <Typography variant="body2">Media tiempo</Typography>
              </SummaryWrapper>

              <SummaryWrapper margin="2px">
                <Tag color="primary" margin="0 10px 0 0" text={totalLitersOperators} />
                <Typography variant="body2">Litros consumidos</Typography>
              </SummaryWrapper>
            </TitleInfo>
          </Wrapper>
        )}

        <Separator />
        <Typography fontSize="12px" lineHeight="16px" variant="body2">
          El ralentí está medido en base a tres criterios: camión <strong>encendido</strong>, <strong>detenido</strong>{' '}
          y <strong>vacío</strong>. El óptimo está definida en el percentil 80, y la tabla a continuación muestra a los
          operadores que exceden dicha media.
        </Typography>
      </Wrapper>
      <Separator />

      {loading && !error && (
        <Wrapper>
          <Skeleton height="63px" margin="0 0 10px" variant="rect" />
          <Skeleton height="63px" margin="0 0 10px" variant="rect" />
          <Skeleton height="63px" margin="0 0 10px" variant="rect" />
          <Skeleton height="63px" margin="0 0 10px" variant="rect" />
        </Wrapper>
      )}

      {!loading && !error && (
        <Wrapper>
          {groups.map((group, index) => {
            return (
              <Accordion
                key={`accordeon-${index}`}
                expanded={expanded === `group-${group.id}`}
                onChange={handleChange(`group-${group.id}`)}
              >
                <AccordionSummary
                  aria-controls={`group-content-${group.id}`}
                  flexWrap="wrap"
                  id={`group-content-${group.id}`}
                >
                  <Typography fontWeight="bold" variant="h4">
                    {group.name.toUpperCase()}
                  </Typography>

                  <Arrow />

                  {!isTiny && (
                    <>
                      <SummaryWrapper margin="2px 30px 2px 0">
                        <Tag color="primary" margin="0 10px 0 0" text={group.p8IdleRpm} />
                        <Typography variant="body2">P80 tiempo en ralentí</Typography>
                      </SummaryWrapper>
                    </>
                  )}
                </AccordionSummary>

                <AccordionDetails>
                  <IdleRpmTable group={group} groupId={`group-${group.id}`} onToggleModal={onToggleModal} />
                </AccordionDetails>
              </Accordion>
            )
          })}
          {!reportLoading && reportSuccess && (
            <CSVLink
              style={{ textDecoration: 'none' }}
              data={reportData[0]}
              filename="base consolidada ralenti.csv"
              ref={csvLink}
              target="_blank"
            >
              <Button color="primary" size="small" variant="outlined">
                Descargar
              </Button>
            </CSVLink>
          )}
          {reportLoading && (
            <Button loading color="primary" size="small" variant="outlined" margin="5px 650px 0 0">
              Cargando
            </Button>
          )}
        </Wrapper>
      )}

      {!loading && error && (
        <Alert severity="default">
          <Strong>Lo sentimos, estamos presentando problemas con nuestro servidor</Strong>
          <br />
          En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
          más.
        </Alert>
      )}
    </Root>
  )
}

IdleRpm.defaultProps = {
  onToggleModal: () => undefined
}
IdleRpm.propTypes = {
  onToggleModal: PropTypes.func
}

export default IdleRpm
